import { Injectable, Inject } from '@angular/core';
import { ConfiguratorsDataService, EventBusService, TranslateService } from '@icc/shared';
import { APP_CONFIG, AppConfigFactory } from '@icc/shared/config';
import { ConfigurationsService, StepsService } from '@icc/configurator/shared';
import { FillingsService } from '../../modules/glazings/fillings.service';
import { IccFilling, DoorActiveConfiguration, ActiveSash } from '@icc/window-door/types';
import { IccPredefinedFillingLayout } from '@icc/shared/types/api/predefined-filling-layout';
import { ReplaySubject } from 'rxjs';
import { SashTypesService } from '../../modules/layout/sash-types.service';
import { FillingsFacade } from '../../glazings/fillings.facade';

@Injectable()
export class ModelService {
    models: IccFilling[] = [];
    models$ = new ReplaySubject<IccFilling[]>();
    sashActiveModels: IccFilling[] = [];
    sashActiveModels$ = new ReplaySubject<IccFilling[]>();
    modelsGroups$ = new ReplaySubject<any[]>();
    loadedData = false;
    modelsGroups = [];
    currentModelGroup = null;

    private isApplyingLayout = false;
    private pendingLayoutData: {
        id: number | string | null;
        name: string;
        image: string;
    } | null = null;
    private endLayoutApplyTimer: any = null;
    private appliedLayoutFingerprint: string | null = null;
    private appliedMullionsFingerprint: string | null = null;
    private layoutInitInProgress = false;
    private layoutInitTimer: any = null;

    constructor(
        @Inject(APP_CONFIG) protected config: AppConfigFactory,
        private configurationsService: ConfigurationsService<'door'>,
        private translateService: TranslateService,
        private eventBusService: EventBusService,
        private configuratorsDataService: ConfiguratorsDataService,
        private stepsService: StepsService,
        private fillingsService: FillingsService,
        private sashTypesService: SashTypesService,
        private fillingsFacade: FillingsFacade,
    ) {
        if (this.configuratorsDataService.loaded) {
            this.init();
        }

        this.eventBusService.subscribeWithoutConfiguration(
            ['initializedConfigurator', 'loadedConfiguratorsData', 'changedSashes', 'loadedFillings', 'validatedProfiles'],
            () => {
                this.init();
            }
        );

        this.eventBusService.subscribeWithoutConfiguration(
            ['setShape', 'changedStep', 'icc-redraw', 'changedSashes', 'setSystem', 'setProfileSet'],
            () => {
                this.ensureSelectedLayoutFits();
            }
        );

        this.eventBusService.subscribe<{ newSystem: boolean }>('setSystem', data => {
            if (data.value?.newSystem && DoorActiveConfiguration.is(data.activeConfiguration)) {
                this.clearPredefinedLayoutSelection(data.activeConfiguration);
            }
        });

        this.eventBusService.subscribeWithoutConfiguration(
            [
                'icc-redraw',
                'changedSashes',
                'setGlazingInSash',
                'putAlignmentInField',
                'removedAlignmentInField',
                'removedAlignment',
                'changedFillings',
                'changeSlat',
                'changedFrames',
                'setFrameProfile',
                'setMullionProfile',
                'setProfileSet',
            ],
            () => {
                this.clearPredefinedLayoutNameOnEdit();
            }
        );
    }

    beginLayoutApply(data?: { id: number | string | null; name: string; image: string }) {
        this.isApplyingLayout = true;
        if (data) {
            this.pendingLayoutData = { ...data };
        }
        if (this.endLayoutApplyTimer) {
            clearTimeout(this.endLayoutApplyTimer);
            this.endLayoutApplyTimer = null;
        }
    }

    endLayoutApply() {
        if (this.endLayoutApplyTimer) {
            clearTimeout(this.endLayoutApplyTimer);
        }
        this.endLayoutApplyTimer = setTimeout(() => {
            this.isApplyingLayout = false;
            this.pendingLayoutData = null;
            this.endLayoutApplyTimer = null;
            this.appliedLayoutFingerprint = this.computeLayoutFingerprint();
            this.appliedMullionsFingerprint = this.computeMullionsFingerprint();
        }, 0);
    }

    private computeMullionsFingerprint(): string | null {
        const conf = this.configurationsService.conf.Current;
        if (!DoorActiveConfiguration.is(conf)) {
            return null;
        }
        const mainSash = this.fillingsFacade.getDoorSash(conf, 'doorActive');
        if (!mainSash) {
            return null;
        }
        const sashMullions = ((mainSash as any).intMullions as any[]) || [];
        const mullions = sashMullions.length ? sashMullions : ((conf as any).Mullions || []);
        return JSON.stringify(
            mullions.map(m => {
                const direction = m?.direction || m?.directionType;
                const p = direction === 'horizontal'
                    ? Math.round((m?.ry ?? m?.position ?? m?.rx ?? 0) as number)
                    : Math.round((m?.rx ?? m?.position ?? m?.ry ?? 0) as number);
                return {
                    id: m?.id ?? null,
                    d: direction,
                    p,
                };
            }),
        );
    }

    private computeLayoutFingerprint(): string | null {
        const conf = this.configurationsService.conf.Current;
        if (!DoorActiveConfiguration.is(conf)) {
            return null;
        }
        const mainSash = this.fillingsFacade.getDoorSash(conf, 'doorActive');
        if (!mainSash) {
            return null;
        }
        const intSashes = (mainSash.intSashes as any[]) || [];
        const sashMullions = ((mainSash as any).intMullions as any[]) || [];
        const sashAlignments = ((mainSash as any).intAlignments as any[]) || [];
        const mullions = sashMullions.length ? sashMullions : ((conf as any).Mullions || []);
        const alignments = sashAlignments.length ? sashAlignments : ((conf as any).Alignments || []);
        const getPosition = (item: any) => {
            const direction = item?.direction || item?.directionType;
            if (direction === 'horizontal') {
                return Math.round((item?.ry ?? item?.position ?? item?.rx ?? 0) as number);
            }
            return Math.round((item?.rx ?? item?.position ?? item?.ry ?? 0) as number);
        };
        const fp = {
            s: intSashes.map(s => ({
                w: Math.round(s.rWidth || 0),
                h: Math.round(s.rHeight || 0),
                t: s.type?.type || s.type?.id || null,
                f: s.glazing?.id || s.filling?.id || null,
            })),
            m: mullions.map(m => ({
                d: m.direction || m.directionType,
                p: getPosition(m),
            })),
            a: alignments.map(a => ({
                d: a.direction || a.directionType,
                p: getPosition(a),
            })),
        };
        return JSON.stringify(fp);
    }

    private clearPredefinedLayoutNameOnEdit() {
        const conf = this.configurationsService.conf.Current;
        if (!DoorActiveConfiguration.is(conf) || !conf.ModelOptions) {
            return;
        }
        const opts = conf.ModelOptions;
        if (this.isApplyingLayout && this.pendingLayoutData) {
            const rawId = this.pendingLayoutData.id;
            const numId = rawId != null ? Number(rawId) : undefined;
            opts.predefinedLayoutId = Number.isFinite(numId) ? numId : undefined;
            opts.predefinedLayoutName = this.pendingLayoutData.name;
            opts.predefinedLayoutImage = this.pendingLayoutData.image;
            opts.hasBeenChosen = true;
            return;
        }
        if (this.isApplyingLayout) {
            return;
        }
        if (opts.predefinedLayoutName || opts.predefinedLayoutId || opts.predefinedLayoutImage) {
            if (this.layoutInitInProgress) {
                return;
            }
            if (
                this.appliedLayoutFingerprint === null
                && this.appliedMullionsFingerprint === null
            ) {
                return;
            }
            const currentFp = this.computeLayoutFingerprint();
            const currentMullionsFp = this.computeMullionsFingerprint();
            const layoutChanged =
                currentFp !== null
                && this.appliedLayoutFingerprint !== null
                && this.appliedLayoutFingerprint !== currentFp;
            const mullionsChanged =
                currentMullionsFp !== null
                && this.appliedMullionsFingerprint !== null
                && this.appliedMullionsFingerprint !== currentMullionsFp;
            if (!layoutChanged && !mullionsChanged) {
                return;
            }
            this.clearPredefinedLayoutSelection(conf);
        }
    }

    private clearPredefinedLayoutSelection(conf: DoorActiveConfiguration) {
        this.fillingsService.clearPredefinedLayoutMeta(conf);
        this.appliedLayoutFingerprint = null;
        this.appliedMullionsFingerprint = null;
    }

    private init() {
        const confForLog = this.configurationsService.conf?.Current;
        const optsForLog = DoorActiveConfiguration.is(confForLog) ? confForLog.ModelOptions : null;
        this.models = this.getModels();
        this.sashActiveModels = this.getModels('doorActive');
        this.models$.next(this.models);
        this.sashActiveModels$.next(this.sashActiveModels);
        this.modelsGroups = this.getModelsGroups();
        this.modelsGroups$.next(this.modelsGroups);
        this.loadedData = true;
        this.layoutInitInProgress = true;
        if (this.layoutInitTimer) {
            clearTimeout(this.layoutInitTimer);
        }
        this.layoutInitTimer = setTimeout(() => {
            this.layoutInitInProgress = false;
            this.layoutInitTimer = null;
            const conf = this.configurationsService.conf.Current;
            const fp = DoorActiveConfiguration.is(conf) && conf.ModelOptions?.predefinedLayoutId
                ? this.computeLayoutFingerprint()
                : null;
            const mullionsFp = DoorActiveConfiguration.is(conf) && conf.ModelOptions?.predefinedLayoutId
                ? this.computeMullionsFingerprint()
                : null;
            if (fp !== null) {
                this.appliedLayoutFingerprint = fp;
            }
            if (mullionsFp !== null) {
                this.appliedMullionsFingerprint = mullionsFp;
            }
        }, 0);
    }
    
    get predefinedFillingLayouts(): IccPredefinedFillingLayout[] {
        return this.configuratorsDataService.data.predefinedFillingLayouts as IccPredefinedFillingLayout[];
    }

    /**
     * Zwracanie modelu
     * @return {Object} Model drzwi
     */
    getModels(sashType?: 'doorActive') {
        const conf = this.configurationsService.conf.Current;
        let models = this.fillingsService
            .getMatchingFillingsForDoorSashes(conf, sashType)
            .filter(f => f.type === (conf.System.door_type ? 'door_panels' : 'deco_panels'));

       return this.fillingsService.getUniqueOrderedFillings(models);
    }

    /**
     * Zwracanie grup modeli
     * @return {Object} Grupy modeli
     */
    getModelsGroups() {
        return this.fillingsService.fillingsCategories;
    }

    /**
     * Wybór modelu
     * @param  {Object} model    Model
     * @param  {Boolean} nextStep [description]
     * @param  {Number} price    Cena
     */
    selectModel(model: IccFilling, sashType?) {
        return this.fillingsService.setDecorativePanelInDoorSashes(
            this.configurationsService.conf.Current,
            this.configurationsService.conf.Default,
            model,
            sashType
        );
    }

    private ensureSelectedLayoutFits() {
        const conf = this.configurationsService.conf.Current;
        if (!DoorActiveConfiguration.is(conf)) {
            return;
        }
        const layoutId = conf.ModelOptions && (conf.ModelOptions as any).predefinedLayoutId;
        if (!layoutId) {
            return;
        }
        const layout = (this.predefinedFillingLayouts || []).find(
            l => String(l.id) === String(layoutId),
        );
        if (!layout) {
            return;
        }
        const mainSash = this.fillingsFacade.getDoorSash(conf, 'doorActive');
        if (!mainSash) {
            return;
        }
        const width = Number((mainSash as any).rWidth) || 0;
        const height = Number((mainSash as any).rHeight) || 0;
        const systemId = Number((conf as any)?.System?.id) || 0;
        const unavailableForSystem =
            systemId
            && Array.isArray((layout as any).unavailable_systems)
            && (layout as any).unavailable_systems.map(Number).includes(systemId);
        const outOfRange =
            (layout.min_width && width && width < Number(layout.min_width))
            || (layout.max_width && width && width > Number(layout.max_width))
            || (layout.min_height && height && height < Number(layout.min_height))
            || (layout.max_height && height && height > Number(layout.max_height));
        if (!outOfRange && !unavailableForSystem) {
            return;
        }
        this.resetSashLayout(conf, mainSash);
        if (conf.ModelOptions) {
            this.clearPredefinedLayoutSelection(conf);
        }
        this.eventBusService.post({ key: 'changedSashes', value: {}, conf });
        this.eventBusService.post({ key: 'icc-redraw', value: 'frame', conf });
    }

    private resetSashLayout(conf: DoorActiveConfiguration, sash: ActiveSash) {
        sash.intSashes = [];
        sash.intMullions = [];
        sash.intAlignments = [];
        sash.intEdgeSashes = { top: [], bottom: [], left: [], right: [] };
        this.sashTypesService.setType(sash, sash.type, conf);
        const matching = this.fillingsService.getMatchingFillingsForDoorSashes(
            conf,
            'doorActive',
            true,
        ) || [];
        const glazings = matching.filter((f: any) => f.type === 'glazing');
        const defaultGlassId = (conf.System as any)?.default_market_configuration?.glass_type_id
            ?? (conf.System as any)?.default_glass_type_id;
        const glazing = (defaultGlassId && glazings.find(
            (f: any) => Number(String(f.id).split('.')[0]) === Number(defaultGlassId),
        )) || glazings[0] || null;
        if (glazing) {
            const field = sash.intSashes && sash.intSashes[0] ? sash.intSashes[0] : sash;
            this.fillingsService.setFillingInSash(field, sash, glazing, conf, false, 'doorActive');
        }
    }
}
